import { useAuthContext } from "../../auth/state/AuthContext";
import { RequestFailModal } from "../../certificates/components/RequestFailModal";
import { RequestSentModal } from "../../certificates/components/RequestSentModal";
import { docTypes } from "../../certificates/const/doc-types.const";
import { CertificateDocumentPage, DropdownVessel } from "../../certificates/models/certificates.interfaces";
import Header from "../../shared/app-shell/Header";
import Sidebar from "../../shared/app-shell/Sidebar";
import { documentsAtom, featureFlagsAtom, loadingAtom, themeAtom, userAtom } from "../../shared/atoms";
import LoadingIndicator from "../../shared/components/LoadingIndicator";
import { toastError } from "../../shared/components/ToastContainer";
import { log, logE, logV } from "../../shared/services/debug-logger";
import { getDateOnly } from "../../shared/services/utils";
import FormStepOne from "../components/UploadFile/FormStepOne";
import FormStepTwo from "../components/UploadFile/FormStepTwo";
import { DocumentMetadataExtract } from "../models/documents.models";
import { updateDocumentInformation, uploadDocument } from "../services/documents.service";
import { applyUserSelectedVesselIdentityOnImoMismatch } from "../utils/document-upload.utils";
import { datadogRum } from "@datadog/browser-rum";
import axios from "axios";
import { useAtom, useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";

const isModalDismissedAtom = atomWithStorage("isDocumentUploadConfirmationModalDismissed", false);

const getErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError<{ error?: string; message?: string } | string>(err)) {
    const responseData = err.response?.data;

    if (typeof responseData === "string") return responseData;

    return responseData?.error ?? responseData?.message ?? err.message;
  }

  return err instanceof Error ? err.message : String(err);
};

const DocumentPage = () => {
  const theme = useAtomValue(themeAtom);

  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useAtom(loadingAtom);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalDimissed, setIsModalDimissed] = useAtom(isModalDismissedAtom);
  const [documents, setDocuments] = useAtom(documentsAtom);
  const [opsLensDocData, setOpsLensDocData] = useState<CertificateDocumentPage[]>([]);
  const uploadAbortController = useRef<AbortController | null>(null);
  const userSelectedVessels = useRef(new Map<string, DropdownVessel>());
  const [uploadDocumentsPristine, setUploadDocumentsPristine] = useState(true);
  const [extractionProgressByFileName, setExtractionProgressByFileName] = useState<Record<string, number>>({});
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const { sub: email } = useAuthContext().state.user?.accessToken || {};
  const featureFlags = useAtomValue(featureFlagsAtom);
  logV("DocumentPage - BUILD", { step, count: documents.length, loading: isLoading });

  const handlePrevStep = () => {
    log("DocumentPage - handlePrevStep");
    setStep((prevState) => prevState - 1);
  };

  const handleNextStep = () => {
    setStep((prevState) => prevState + 1);
  };

  const clearDocuments = () => {
    log("STATE - clearDocuments");
    setDocuments([]);
  };

  const handleUserVesselSelected = (fileName: string, vessel: DropdownVessel) => {
    if (fileName) userSelectedVessels.current.set(fileName, vessel);
  };

  useEffect(() => {
    return () => {
      uploadAbortController.current?.abort();
      clearDocuments();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadDocuments = async () => {
    if (isLoading) return;
    log("DocumentPage - uploadDocuments", { count: documents.length });

    setErrors(null); // Clear prev error
    setIsModalVisible(false);
    setUploadDocumentsPristine(false);
    const abortController = new AbortController();
    uploadAbortController.current = abortController;
    try {
      setIsLoading(true);
      const extractedDocs: DocumentMetadataExtract[] = [];

      for (let index = 0; index < documents.length; index += 1) {
        const doc = documents[index];
        extractedDocs.push(
          await uploadDocument(doc, user, abortController.signal, (progress) =>
            setExtractionProgressByFileName((currentProgress) => ({
              ...currentProgress,
              [doc.cachedFile.name]: progress.totalPages
                ? Math.round(((progress.processedPages ?? 0) / progress.totalPages) * 100)
                : 0,
            })),
          ),
        );
      }

      const documentsUpdated = documents.map((doc: CertificateDocumentPage, index: number) => {
        const extractedData = extractedDocs[index];
        const nextSurveyDate =
          !doc.nextSurveyDate && extractedData.nextSurveyDate
            ? null
            : !!extractedData.nextSurveyDate?.value
              ? extractedData.nextSurveyDate.value
              : null;

        return {
          ...doc,
          s3Path: extractedData.s3Path,
          vesselName: extractedData.associatedVessel?.name ?? doc.vesselName,
          vesselCode: extractedData.associatedVessel?.code ?? doc.vesselCode,
          imoNumber: extractedData.associatedVessel?.imoNumber ?? doc.imoNumber,
          vesselMasterEmail: extractedData.associatedVessel?.email ?? doc.vesselMasterEmail,
          expiresOn: !!extractedData.validityEndDate?.value ? extractedData.validityEndDate.value : null,
          nextSurveyDate,
          hasSurveyDate: !!extractedData.nextSurveyDate?.value,
          docName: !!extractedData.documentName?.value ? extractedData.documentName.value : doc.docName,
          fileName: doc.cachedFile.name,
        };
      });

      log("STATE - uploadDocuments - apply", { count: documentsUpdated.length });
      setDocuments(documentsUpdated);
      setOpsLensDocData(documentsUpdated);
      handleNextStep();
    } catch (err) {
      if (abortController.signal.aborted) {
        log("DocumentPage - uploadDocuments - cancelled");
        return;
      }
      logE(new Error("DocumentPage - uploadDocuments - failed"));
      const error = new Error(getErrorMessage(err));
      setErrors(error);
      setIsModalVisible(true);
    } finally {
      if (uploadAbortController.current === abortController) {
        uploadAbortController.current = null;
      }
      setIsLoading(false);
      setExtractionProgressByFileName({});
    }
  };

  /* Added logic to add document upload properties success rate to DataDog */
  const sendDocSuccessRateToDataDog = () => {
    logV("DocumentPage - sendDocSuccessRateToDataDog", { count: documents.length });
    try {
      documents.forEach((doc) => {
        datadogRum.addAction("uploadDocument", {
          ext_user_email: email,
          documents: documents.map((doc) => doc.docName),
        });
        const opsDoc = opsLensDocData.find((d) => d.s3Path === doc.s3Path);
        if (opsDoc) {
          // Business logic checks for doc1
          const docType1 = docTypes.find((dt) => dt.name === doc.docName);
          const requiresSurveyDate1 = docType1?.hasSurveyDate ?? false;

          const hasVesselName1 = doc.vesselName.trim() !== "";
          const hasName1 = doc.docName.trim() !== "";
          const expiryDateValid1 = doc.expiryNotApplicable ? true : !!doc.expiresOn;
          const hasSurveyDate1 = requiresSurveyDate1 ? !!doc.nextSurveyDate : true;

          // Business logic checks for doc2
          const docType2 = docTypes.find((dt) => dt.name === opsDoc.docName);
          const requiresSurveyDate2 = docType2?.hasSurveyDate ?? false;

          const hasVesselName2 = opsDoc.vesselName.trim() !== "";
          const hasName2 = opsDoc.docName.trim() !== "";
          const expiryDateValid2 = opsDoc.expiryNotApplicable ? true : !!opsDoc.expiresOn;
          const hasSurveyDate2 = requiresSurveyDate2 ? !!opsDoc.nextSurveyDate : true;

          // Compare each business rule
          const sameVessel = hasVesselName1 === hasVesselName2 && doc.vesselName === opsDoc.vesselName;
          const sameName = hasName1 === hasName2 && doc.docName === opsDoc.docName;
          const sameExpiry =
            expiryDateValid1 === expiryDateValid2 && (doc.expiresOn ?? "") === (opsDoc.expiresOn ?? "");
          const sameSurvey =
            hasSurveyDate1 === hasSurveyDate2 &&
            (getDateOnly(doc.nextSurveyDate) ?? "") === (getDateOnly(opsDoc.nextSurveyDate) ?? "");

          const matchedFields = [sameVessel, sameName, sameExpiry, sameSurvey].filter(Boolean).length;

          if (matchedFields === 4) {
            datadogRum.addAction("documentUploadStatus", {
              ext_user_email: email,
              status: "success",
            });
          } else if (matchedFields > 0) {
            datadogRum.addAction("documentUploadStatus", {
              ext_user_email: email,
              status: "partialsuccess",
            });
          } else if (matchedFields === 0) {
            datadogRum.addAction("documentUploadStatus", {
              ext_user_email: email,
              status: "failure",
            });
          }
        }
      });
    } catch (error) {
      logE(new Error("DocumentPage - sendDocSuccessRateToDataDog - failed"));
    }
  };

  const updateDocumentsInformation = async () => {
    if (isLoading) return;
    log("DocumentPage - updateDocumentsInformation", { count: documents.length });

    setErrors(null);
    setIsModalVisible(false);
    //update document upload info.

    const hasAllFieldsWithData = documents.every((doc) => {
      const hasVesselName = doc.vesselName.trim() !== "";
      const hasName = doc.docName.trim() !== "";
      const expiryDateValid = doc.expiryNotApplicable ? true : !!doc.expiresOn;
      const docType = docTypes.find((dt) => dt.name === doc.docName);
      const requiresSurveyDate = docType?.hasSurveyDate ?? false;
      const hasSurveyDate = requiresSurveyDate ? !!doc.nextSurveyDate : true;

      return hasVesselName && hasName && expiryDateValid && hasSurveyDate;
    });

    if (!hasAllFieldsWithData) {
      log("DocumentPage - updateDocumentsInformation - validation blocked");
      toastError("There are some missing fields. Please complete before proceeding.");
      return;
    }
    sendDocSuccessRateToDataDog();
    try {
      setIsLoading(true);

      for (const doc of documents) {
        const opsLensDocument = opsLensDocData.find((opsDoc) => opsDoc.s3Path === doc.s3Path);
        const userSelectedVessel = userSelectedVessels.current.get(doc.fileName);
        const documentToUpdate = applyUserSelectedVesselIdentityOnImoMismatch(doc, opsLensDocument, userSelectedVessel);

        await updateDocumentInformation(documentToUpdate, user);
      }

      if (isModalDimissed) {
        log("DocumentPage - updateDocumentsInformation - redirect");
        navigate("/certificates");
      } else {
        log("DocumentPage - updateDocumentsInformation - confirmation");
        setIsModalVisible(true);
      }
    } catch (err) {
      logE(new Error("DocumentPage - updateDocumentsInformation - failed"));
      const error = new Error(getErrorMessage(err));
      setErrors(error);
      setIsModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepForm = (step: number): JSX.Element => {
    logV("DocumentPage - renderStepForm", { step });
    switch (step) {
      case 1:
        return <FormStepOne progressByFileName={extractionProgressByFileName} />;
      case 2:
        return (
          <FormStepTwo
            documents={documents}
            pristine={uploadDocumentsPristine}
            onVesselSelected={handleUserVesselSelected}
          />
        );
      default:
        return <FormStepOne />;
    }
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div
        className={twMerge(
          "flex flex-1 flex-col overflow-y-hidden",
          theme.home.tailwind.backgound,
          theme.home.tailwind.textDefault,
        )}
      >
        <Header />

        <div className="flex w-full flex-1 flex-col overflow-hidden px-[24px] pt-2">{renderStepForm(step)}</div>

        <div className={twMerge("flex h-[82px] w-full items-center border-t", theme.uwhcAssistant.border)}>
          <button
            disabled={isLoading}
            className={twMerge(
              "ml-[40px] mr-auto rounded border px-4 py-2 text-sm disabled:opacity-50",
              theme.uwhcAssistant.border,
              theme.uwhcAssistant.text,
            )}
            onClick={() => {
              uploadAbortController.current?.abort();
              navigate("/certificates");
            }}
          >
            Cancel and exit
          </button>
          {step > 1 && (
            <button
              disabled={isLoading}
              onClick={handlePrevStep}
              className={twMerge(
                "ml-auto mr-[24px] rounded border px-4 py-2 text-sm disabled:opacity-50",
                theme.headerComponent.notifications.itemBackground,
                theme.headerComponent.logInCircle.text,
                theme.myVesselsTableRow.row.hoverTwenty,
                theme.uwhcAssistant.border,
              )}
            >
              Previous
            </button>
          )}
          {step === 1 && (
            <button
              disabled={documents.length === 0 || isLoading}
              onClick={uploadDocuments}
              className={twMerge(
                "mr-[40px] flex items-center justify-center rounded border px-4 py-2 text-sm disabled:opacity-50",
                theme.crtDropdown.openBg,
                theme.warningFunction.text,
                theme.crtDropdown.hoverGreen,
                theme.uwhcAssistant.border,
              )}
            >
              {isLoading && featureFlags.opslens_enabled ? (
                <p className="mr-2"> 🔎 Analyzing & Extracting data...</p>
              ) : (
                ""
              )}
              {isLoading ? (
                <LoadingIndicator className="h-6 w-6" />
              ) : featureFlags.opslens_enabled ? (
                "Upload & Analyze documents"
              ) : (
                "Upload"
              )}
            </button>
          )}
          {step === 2 && (
            <button
              disabled={isLoading}
              onClick={updateDocumentsInformation}
              className={twMerge(
                "mr-[40px] flex w-[200px] items-center justify-center rounded border px-4 py-2 text-center text-sm disabled:opacity-50",
                theme.crtDropdown.openBg,
                theme.warningFunction.text,
                theme.crtDropdown.hoverGreen,
                theme.uwhcAssistant.border,
              )}
            >
              {isLoading ? <LoadingIndicator className="h-6 w-6" /> : "Upload documents"}
            </button>
          )}
        </div>
      </div>

      <div style={{ zIndex: "200" }}>
        {!errors && isModalVisible && (
          <RequestSentModal
            isModalOpen={isModalVisible}
            title="Documents uploaded successfully"
            caption="The document can now be found in the certificates and documents table"
            onDismiss={(isHideMessageChecked) => {
              if (isHideMessageChecked) {
                setIsModalDimissed(true);
              }
              navigate("/certificates");
            }}
          />
        )}

        {errors && isModalVisible && (
          <RequestFailModal
            isModalOpen={isModalVisible}
            title="Documents upload failed"
            caption={`It seems like there was an issue with the upload.
              Please double-check your document and try uploading it again.
              If the issue persists, feel free to contact support for assistance (including following detail).
              Detail: "${errors.message}`}
            onDismiss={() => setIsModalVisible(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentPage;
